import React from 'react';
import {
    ArchiveIcon,
    ArchiveRestoreIcon,
    BellIcon,
    BellOffIcon,
    CircleCheckBigIcon,
    EllipsisVerticalIcon,
    ListIcon,
    MailIcon,
    MailOpenIcon,
    StarIcon,
    StarOffIcon,
} from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@rocco/components/dropdown-menu';
import { useConversation } from '../../context';
import { useUpdateConversation } from '../../hooks/use-update-conversation';
import { NonParticipantTooltip } from '../NonParticipantTooltip';

/**
 * The more options button for a chat box. Shows a dropdown menu with more
 * options. Renders nothing when the conversation is not yet loaded.
 * @see {@link ChatBox}
 */
export function ChatBoxMoreOptions({
    className,
    onToggleRead,
}: {
    className?: string;
    onToggleRead: () => void;
}) {
    const { conversationID, professionalID, conversation, isParticipant } =
        useConversation();
    const { markImportant, archiveConversation, markDone, muteConversation } =
        useUpdateConversation({
            conversationID,
            professionalID,
        });

    if (!conversation) {
        return null;
    }

    const isUnread = conversation.UnreadMessageCount > 0;

    if (!isParticipant) {
        return (
            <NonParticipantTooltip asChild>
                <span className="inline-flex">
                    <TriggerButton className={className} disabled />
                </span>
            </NonParticipantTooltip>
        );
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <TriggerButton className={className} />
            </DropdownMenuTrigger>
            <DropdownMenuContent
                className="w-unset min-w-[180px]"
                align="end"
                side="bottom"
            >
                <DropdownMenuItem
                    className="gap-[8px] focus:bg-black/10"
                    onClick={() => onToggleRead()}
                >
                    {isUnread ? (
                        <MailOpenIcon className="size-[14px]" />
                    ) : (
                        <MailIcon className="size-[14px]" />
                    )}
                    <span>{isUnread ? 'Mark as read' : 'Mark as unread'}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    className="gap-[8px] focus:bg-black/10"
                    onClick={() => muteConversation(!conversation.IsMuted)}
                >
                    {conversation.IsMuted ? (
                        <BellIcon className="size-[14px]" />
                    ) : (
                        <BellOffIcon className="size-[14px]" />
                    )}
                    <span>
                        {conversation.IsMuted
                            ? 'Unmute conversation'
                            : 'Mute conversation'}
                    </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    className="gap-[8px] focus:bg-black/10"
                    onClick={() => markImportant(!conversation.IsImportant)}
                >
                    {conversation.IsImportant ? (
                        <StarOffIcon className="size-[14px]" />
                    ) : (
                        <StarIcon className="size-[14px]" />
                    )}
                    <span>
                        {conversation.IsImportant
                            ? 'Unmark as important'
                            : 'Mark as important'}
                    </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    className="gap-[8px] focus:bg-black/10"
                    onClick={() =>
                        archiveConversation(!conversation.IsArchived)
                    }
                >
                    {conversation.IsArchived ? (
                        <ArchiveRestoreIcon className="size-[14px]" />
                    ) : (
                        <ArchiveIcon className="size-[14px]" />
                    )}
                    <span>
                        {conversation.IsArchived
                            ? 'Unarchive conversation'
                            : 'Archive conversation'}
                    </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    className="gap-[8px] focus:bg-black/10"
                    onClick={() => markDone(!conversation.IsDone)}
                >
                    {conversation.IsDone ? (
                        <ListIcon className="size-[14px]" />
                    ) : (
                        <CircleCheckBigIcon className="size-[14px]" />
                    )}
                    <span>
                        {conversation.IsDone
                            ? 'Unmark as done'
                            : 'Mark as done'}
                    </span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

const TriggerButton = React.forwardRef<
    HTMLButtonElement,
    React.ComponentPropsWithRef<'button'>
>(function TriggerButton({ className, ...props }, ref) {
    return (
        <button
            ref={ref}
            type="button"
            className={cn(
                'grid size-[36px] place-items-center rounded-full outline-black/20! hover:bg-black/10 focus-visible:outline! disabled:pointer-events-none disabled:opacity-50',
                className,
            )}
            aria-label="More options"
            {...props}
        >
            <EllipsisVerticalIcon className="size-[16px]" />
        </button>
    );
});
